import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Rating,
  Stack,
  Typography,
  alpha,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  CompareArrows,
  FitnessCenter as FitnessCenterIcon,
  FlagOutlined,
  DeleteOutline,
  StarBorder,
  DirectionsRun
} from '@mui/icons-material';
import DynamicPerformanceChart from 'components/performance/DynamicPerformanceChart';
import PlayCompareModal from 'components/play/performance/PlayCompareModal';
import ImageViewModal from 'components/shared/modals/ImageViewModal';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import { MY_PLAY_RESULTS, MY_PLAY_WORKOUTS, DELETE_SELF_REPORTED_RESULT } from 'api/graphql/play';
import { createSnack } from 'common/utils/general';
import { parseJsonArray } from 'common/utils/graphqlJson';
import uiSlice from 'store/modules/ui';
import { metricLabel, muscleLabel } from 'components/play/constants';
import { formatDuration, repsLabelOf, secondsDoneTotal } from 'components/play/workoutEstimate';

// Suficiente historial para cubrir los entrenamientos recientes de un ejercicio.
const RECENT_WORKOUTS = 50;

// Las dos listas del detalle crecen sin techo (50 entrenamientos, N registros) y empujaban la
// gráfica fuera de la vista. Se muestran 4 filas (3 en móvil, donde el diálogo va a pantalla
// completa) y el resto entra por scroll interno. Los altos son los reales de cada fila
// (padding + dos líneas) para que el corte caiga justo debajo de la última visible.
const VISIBLE_ROWS = { xs: 3, sm: 4 };
const WORKOUT_ROW_H = 66;
const WORKOUT_GAP = 8;
const RESULT_ROW_H = 80;
const RESULT_GAP = 25;

/**
 * Alto visible de una lista: el de N filas, pero nunca más que una fracción de la ventana.
 * El tope en `vh` es el que sostiene la promesa de la pantalla (la gráfica se ve sin mover el
 * diálogo) en portátiles de 768px de alto, donde 4 filas ya se comerían medio diálogo.
 */
const listMaxHeight = (rowHeight, gap, cap) => ({
  xs: `min(${VISIBLE_ROWS.xs * rowHeight + (VISIBLE_ROWS.xs - 1) * gap}px, ${cap.xs})`,
  sm: `min(${VISIBLE_ROWS.sm * rowHeight + (VISIBLE_ROWS.sm - 1) * gap}px, ${cap.sm})`
});
const WORKOUTS_MAX_H = listMaxHeight(WORKOUT_ROW_H, WORKOUT_GAP, { xs: '32vh', sm: '30vh' });
const RESULTS_MAX_H = listMaxHeight(RESULT_ROW_H, RESULT_GAP, { xs: '38vh', sm: '34vh' });

// La métrica Play → tipo de parámetro que entiende la gráfica del entrenador (reutilizada).
const METRIC_TO_PARAM = { TIME_SECONDS: 'TIME', REPS: 'REPS', DISTANCE_KM: 'DISTANCE' };
const setFor = (metric, value) => {
  if (metric === 'TIME_SECONDS') return { time_seconds: value };
  if (metric === 'REPS') return { repetitions: value };
  if (metric === 'DISTANCE_KM') return { distance_km: value };
  return {};
};
const fmtDate = (iso) => {
  try {
    return format(parseISO(iso), 'd MMM yyyy', { locale: es });
  } catch {
    return '';
  }
};

/**
 * Área con scroll interno: la lista se corta a `maxHeight` y el resto se desliza dentro, sin
 * mover el resto del diálogo. El degradado inferior avisa de que hay más y se apaga al llegar
 * al final; la región es enfocable porque un bloque que solo se recorre con scroll tiene que
 * poder recorrerse también con el teclado.
 */
function ScrollArea({ label, maxHeight, children = null }) {
  const ref = useRef(null);
  const [state, setState] = useState({ scrollable: false, atEnd: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const scrollable = el.scrollHeight - el.clientHeight > 4;
    const atEnd = el.scrollHeight - el.scrollTop - el.clientHeight <= 4;
    setState((prev) => (prev.scrollable === scrollable && prev.atEnd === atEnd ? prev : { scrollable, atEnd }));
  }, []);

  // Las filas traen fotos: el alto real solo se conoce cuando cargan, así que se remide.
  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    Array.from(el.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [measure, children]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        ref={ref}
        onScroll={measure}
        role={state.scrollable ? 'group' : undefined}
        tabIndex={state.scrollable ? 0 : undefined}
        aria-label={state.scrollable ? label : undefined}
        sx={(th) => ({
          maxHeight,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          pr: state.scrollable ? 0.75 : 0,
          borderRadius: 2,
          scrollbarWidth: 'thin',
          scrollbarColor: `${th.palette.scrollbar.thumb} transparent`,
          '&:focus-visible': { outline: '2px solid', outlineColor: th.palette.gold.main, outlineOffset: 2 }
        })}>
        {children}
      </Box>
      <Box
        aria-hidden
        sx={(th) => ({
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: 40,
          pointerEvents: 'none',
          opacity: state.scrollable && !state.atEnd ? 1 : 0,
          transition: 'opacity 200ms cubic-bezier(0.32,0.72,0,1)',
          background: `linear-gradient(to top, ${th.palette.surface.content}, ${alpha(th.palette.surface.content, 0)})`
        })}
      />
    </Box>
  );
}

ScrollArea.propTypes = {
  label: PropTypes.string.isRequired,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]).isRequired,
  children: PropTypes.node
};

/**
 * OTRIX Play — una fila de ejercicio de "Rendimientos" con el MISMO lenguaje visual que el
 * rendimiento del entrenador (DynamicExerciseGroup): card con el nº de registros y, al abrir, un
 * detalle con los entrenamientos hechos, la gráfica de progreso y el historial. Es progreso
 * PERSONAL: Play no compite en un ranking, así que aquí no hay posiciones ni Top 3.
 */
function PlayPerformanceExercise({ group }) {
  const { t } = useTranslation(['play']);
  const dispatch = useDispatch();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  // Derivado del token dorado (mode-aware) en vez del #F9BD4E que estaba escrito a mano.
  const goldBorder = alpha(theme.palette.gold.main, 0.3);

  const [open, setOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [zoom, setZoom] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const { exercise, items } = group;

  // Entrenamientos del historial que incluyeron ESTE ejercicio (con su detalle de series/reps).
  const { data: workoutsData } = useQuery(MY_PLAY_WORKOUTS, {
    variables: { first: RECENT_WORKOUTS },
    skip: !open,
    fetchPolicy: 'cache-and-network'
  });

  const exerciseWorkouts = useMemo(() => {
    const list = workoutsData?.myPlayWorkouts || [];
    return list
      .map((workout) => {
        // `details` es un campo JSON: el escalar del backend lo serializa a STRING.
        const detail = parseJsonArray(workout.details).find(
          (d) => String(d.exercise_id) === String(exercise.id)
        );
        return detail && (detail.sets_done || 0) > 0 ? { workout, detail } : null;
      })
      .filter(Boolean);
  }, [workoutsData, exercise.id]);

  const metrics = useMemo(() => [...new Set(items.map((i) => i.metric))], [items]);
  const chartParams = useMemo(() => metrics.filter((m) => METRIC_TO_PARAM[m]).map((m) => ({ parameter_type: METRIC_TO_PARAM[m] })), [metrics]);
  const chartPerformances = useMemo(
    () => items.map((r) => ({ id: r.id, period_start_date: r.created_at, sets: [setFor(r.metric, Number(r.value))] })),
    [items]
  );
  const sortedItems = useMemo(() => [...items].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)), [items]);

  const [deleteResult, { loading: deleting }] = useMutation(DELETE_SELF_REPORTED_RESULT, {
    refetchQueries: [{ query: MY_PLAY_RESULTS, variables: {} }]
  });

  // Borrar un registro es irreversible y se dispara desde una lista densa: siempre se confirma.
  // El modal sigue abierto mientras corre la mutación, así que `deleting` frena el doble clic.
  const handleDelete = async () => {
    if (!pendingDelete || deleting) return;
    try {
      await deleteResult({ variables: { id: pendingDelete.id } });
      dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `del-${Date.now()}`, variant: 'success', message: t('perf.deleted', { ns: 'play', defaultValue: 'Registro eliminado' }) })));
    } catch {
      dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `del-e-${Date.now()}`, variant: 'error', message: t('perf.deleteError', { ns: 'play', defaultValue: 'No se pudo eliminar' }) })));
    } finally {
      setPendingDelete(null);
    }
  };

  return (
    <>
      {/* Card resumen (mismo look que el rendimiento del entrenador) */}
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          cursor: 'pointer',
          px: { xs: 2, sm: 3 },
          py: 1.75,
          backgroundColor: 'surface.content',
          border: '1px solid',
          borderColor: 'hairline.main',
          borderRadius: '12px',
          transition: 'transform 0.2s cubic-bezier(0.32,0.72,0,1), border-color 0.2s',
          '&:hover': { transform: 'translateY(-2px)', borderColor: 'hairline.strong' },
          '&:active': { transform: 'scale(0.98)' }
        }}>
        <Avatar sx={{ bgcolor: 'gold.soft', border: '1px solid', borderColor: goldBorder, width: 44, height: 44 }}>
          <FitnessCenterIcon sx={{ color: 'gold.main', fontSize: 22 }} />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }} noWrap>
            {exercise.localized_name}
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {exercise.muscle_group && <Chip size="small" label={muscleLabel(t, exercise.muscle_group)} sx={{ bgcolor: 'gold.soft', color: 'gold.main', height: 20 }} />}
            {metrics.map((m) => (
              <Chip key={m} size="small" variant="outlined" label={metricLabel(t, m)} sx={{ height: 20 }} />
            ))}
          </Stack>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h5" sx={{ color: 'gold.main', fontWeight: 800, lineHeight: 1 }}>
            {items.length}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('perf.count', { ns: 'play', count: items.length, defaultValue: `${items.length} registros` })}
          </Typography>
        </Box>
        <ChevronRightIcon sx={{ color: 'text.secondary' }} />
      </Box>

      {/* Detalle */}
      <Dialog open={open} onClose={() => setOpen(false)} fullScreen={fullScreen} maxWidth="md" fullWidth PaperProps={{ sx: { backgroundColor: 'surface.content', backgroundImage: 'none', ...(fullScreen ? {} : { borderRadius: 3, border: '1px solid', borderColor: 'hairline.strong' }) } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 6 }}>
          <Avatar sx={{ bgcolor: 'gold.soft', border: '1px solid', borderColor: goldBorder, width: 40, height: 40 }}>
            <FitnessCenterIcon sx={{ color: 'gold.main', fontSize: 20 }} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }} noWrap>
              {exercise.localized_name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('perf.count', { ns: 'play', count: items.length, defaultValue: `${items.length} registros` })}
            </Typography>
          </Box>
          {/* Comparar dos marcas de este ejercicio (necesita al menos dos). */}
          {items.length >= 2 && (
            <IconButton
              size="small"
              onClick={() => setCompareOpen(true)}
              aria-label={t('compare.title', { ns: 'play', defaultValue: 'Comparar marcas' })}
              sx={{ color: 'gold.main', flex: 'none', border: '1px solid', borderColor: 'hairline.main' }}>
              <CompareArrows fontSize="small" />
            </IconButton>
          )}
          <IconButton onClick={() => setOpen(false)} size="small" sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Entrenamientos en los que hiciste este ejercicio: cuándo, cuánto duró y qué series
              hiciste. Aquí NO va el leaderboard: esta pantalla es tu registro, no una competencia. */}
          {exerciseWorkouts.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <DirectionsRun sx={{ color: 'gold.main', fontSize: 18, flex: 'none' }} />
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600, flex: 1, minWidth: 0 }}>
                  {t('perf.workoutsTitle', { ns: 'play' })}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', flex: 'none', fontVariantNumeric: 'tabular-nums' }}>
                  {t('perf.workoutsCount', {
                    ns: 'play',
                    count: exerciseWorkouts.length,
                    defaultValue: `${exerciseWorkouts.length} entrenamientos`
                  })}
                </Typography>
              </Stack>
              <ScrollArea label={t('perf.workoutsTitle', { ns: 'play' })} maxHeight={WORKOUTS_MAX_H}>
                <Stack spacing={1}>
                  {exerciseWorkouts.map(({ workout, detail }) => {
                    // Tiempo de ESTE ejercicio (suma de sus series). Los entrenamientos anteriores a
                    // la captura por serie no lo traen: ahí se cae al total de la sesión, y la
                    // etiqueta lo dice para no hacer pasar una cosa por la otra.
                    const exerciseSeconds = secondsDoneTotal(detail);
                    return (
                      <Stack
                        key={workout.id}
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'hairline.main', bgcolor: 'surface.overlay' }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {fmtDate(workout.completed_at || workout.created_at)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {t('perf.workoutSets', { ns: 'play', done: detail.sets_done || 0, total: detail.sets || 0 })}
                            {repsLabelOf(detail) ? ` · ${repsLabelOf(detail)} ${t('card.reps', { ns: 'play' })}` : ''}
                          </Typography>
                        </Box>
                        <Stack alignItems="flex-end" spacing={0.25} sx={{ flex: 'none' }}>
                          <Typography variant="body2" fontWeight={800} sx={{ color: 'gold.main', fontVariantNumeric: 'tabular-nums' }}>
                            {formatDuration(exerciseSeconds || workout.total_seconds)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {exerciseSeconds
                              ? t('perf.exerciseTime', { ns: 'play', defaultValue: 'en este ejercicio' })
                              : t('workout.elapsed', { ns: 'play' })}
                          </Typography>
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              </ScrollArea>
            </Box>
          )}

          <Divider sx={{ borderColor: 'hairline.main', mb: 3 }} />

          {/* Gráfica de progreso (misma del entrenador, alimentada con tus registros) */}
          {chartParams.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <DynamicPerformanceChart performances={chartPerformances} params={chartParams} />
            </Box>
          )}

          {/* Historial */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <StarBorder sx={{ color: 'gold.main', fontSize: 18, flex: 'none' }} />
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600, flex: 1, minWidth: 0 }}>
              {t('perf.historyTitle', { ns: 'play', defaultValue: 'Historial de registros' })}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', flex: 'none', fontVariantNumeric: 'tabular-nums' }}>
              {t('perf.count', { ns: 'play', count: items.length, defaultValue: `${items.length} registros` })}
            </Typography>
          </Stack>
          <ScrollArea label={t('perf.historyTitle', { ns: 'play', defaultValue: 'Historial de registros' })} maxHeight={RESULTS_MAX_H}>
            <Stack divider={<Divider flexItem />} spacing={1.5}>
              {sortedItems.map((r) => (
                // A 320 la foto (56) + la caneca se comían ~100px de los ~272 útiles y la línea
                // "45 · Repeticiones · 14 jul" no cabía: la foto se reduce en xs.
                <Stack key={r.id} direction="row" spacing={{ xs: 1, sm: 1.5 }} alignItems="flex-start">
                  {r.photo_url && (
                    <Box component="img" src={r.photo_url} alt="" onClick={() => setZoom(r.photo_url)} sx={{ width: { xs: 40, sm: 56 }, height: { xs: 40, sm: 56 }, borderRadius: 2, objectFit: 'cover', border: '1px solid', borderColor: 'hairline.main', cursor: 'zoom-in', flex: 'none' }} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                      <Typography variant="body1" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.value}</Typography>
                      <Typography variant="caption" color="text.secondary">{metricLabel(t, r.metric)}</Typography>
                      <Typography variant="caption" color="text.secondary">· {fmtDate(r.created_at)}</Typography>
                      {r.is_flagged && <Chip size="small" icon={<FlagOutlined sx={{ fontSize: 14 }} />} label={t('ranking.inReview', { ns: 'play', defaultValue: 'En revisión' })} sx={{ bgcolor: 'gold.soft', color: 'gold.main' }} />}
                      {/* Resultado creado por el motor de entrenamiento (no enviado a mano con foto). */}
                      {r.source === 'workout' && (
                        <Chip
                          size="small"
                          icon={<DirectionsRun sx={{ fontSize: 14 }} />}
                          label={t('perf.fromWorkout', { ns: 'play' })}
                          sx={{ bgcolor: 'surface.overlay', color: 'text.secondary' }}
                        />
                      )}
                    </Stack>
                    {r.rating ? (
                      <Box sx={{ mt: 0.75 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Rating value={r.rating} max={5} readOnly size="small" sx={{ color: 'gold.main' }} />
                          {r.rater?.full_name && (
                            <Typography variant="caption" color="text.secondary">{t('perf.ratedBy', { ns: 'play', name: r.rater.full_name, defaultValue: `Calificó ${r.rater.full_name}` })}</Typography>
                          )}
                        </Stack>
                        {r.feedback && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>{r.feedback}</Typography>}
                      </Box>
                    ) : (
                      <Chip size="small" icon={<StarBorder sx={{ fontSize: 14 }} />} variant="outlined" label={t('perf.notRated', { ns: 'play', defaultValue: 'Sin calificar' })} sx={{ mt: 0.75, color: 'text.secondary' }} />
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    aria-label={t('perf.delete', { ns: 'play', defaultValue: 'Eliminar' })}
                    onClick={() => setPendingDelete(r)}
                    sx={{
                      flex: 'none',
                      color: 'deleteAction.fg',
                      transition: 'transform 0.2s cubic-bezier(0.32,0.72,0,1), background-color 0.2s',
                      '&:hover': { backgroundColor: 'deleteAction.bg' },
                      '&:active': { transform: 'scale(0.98)' }
                    }}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <ImageViewModal open={Boolean(zoom)} src={zoom} alt={t('result.evidenceAlt', { ns: 'play', defaultValue: 'Evidencia' })} onClose={() => setZoom(null)} />

      <ConfirmDeleteModal
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleDelete}
        title={t('perf.deleteTitle', { ns: 'play', defaultValue: 'Eliminar registro' })}
        message={
          pendingDelete
            ? t('perf.deleteMessage', {
                ns: 'play',
                value: pendingDelete.value,
                metric: metricLabel(t, pendingDelete.metric),
                date: fmtDate(pendingDelete.created_at),
                defaultValue: `Se eliminará tu registro de ${pendingDelete.value} ${metricLabel(t, pendingDelete.metric)} del ${fmtDate(pendingDelete.created_at)}.`
              })
            : ''
        }
        note={t('perf.deleteNote', { ns: 'play', defaultValue: 'Esta acción no se puede deshacer.' })}
      />

      <PlayCompareModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        exerciseName={exercise.localized_name}
        results={items}
      />
    </>
  );
}

PlayPerformanceExercise.propTypes = {
  group: PropTypes.shape({
    exercise: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      localized_name: PropTypes.string,
      muscle_group: PropTypes.string
    }).isRequired,
    items: PropTypes.array.isRequired
  }).isRequired
};

export default PlayPerformanceExercise;
